<template>
  <tr
    class="clickable"
    :class="{ 'red lighten-5': player.hasBigInjury }"
    @click="onClick"
  >
    <td
      style="width: 0px"
      class="pr-0"
    >
      <v-avatar
        size="32px"
        class="mr-2"
        color="grey lighten-2"
      >
        <v-img :src="player.photo_url">
          <template v-slot:placeholder>
            <v-icon x-small>
              fal fa-user
            </v-icon>
          </template>
        </v-img>
      </v-avatar>
    </td>
    <td style="max-width: 150px">
      <v-row
        no-gutters
        align="center"
      >
        <v-col>
          {{ player.last_name }}
          {{ player.first_name }}
        </v-col>
        <v-col cols="auto">
          <v-tooltip
            v-if="player.smart_assistant.length"
            top
          >
            <template v-slot:activator="{ on, attrs }">
              <span
                v-bind="attrs"
                v-on="on"
              >
                <v-badge
                  :content="player.smart_assistant.length"
                  color="primary"
                  inline
                  :value="player.smart_assistant.length"
                />
              </span>
            </template>
            <span>{{ $tc('tooltip.smart_assistant', player.smart_assistant.length, { num: player.smart_assistant.length }) }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </td>
    <td v-if="!isIndividualSport">
      <div v-if="player.player_positions && player.player_positions.length > 0">
        <v-chip
          v-for="position in player.player_positions"
          :key="player.id + position.id"
          small
          label
        >
          {{ position.short_name }}
        </v-chip>
      </div>

      <span
        v-else
        class="grey--text caption"
      >N/A</span>
    </td>
    <td>
      <v-chip
        v-if="player.player_status"
        small
        :color="player.player_status.name === 'healthy' ? 'success' : 'error lighten-3'"
        dark
        label
      >
        {{ $t(`player_status.${player.player_status.name}`) }}
      </v-chip>
    </td>
    <td v-if="hasItlModule">
      <table-bar-value
        :value="player.latest_stats.ACWR"
        :max="maxValues.ACWR"
        :color="getAcwrColorFromValue(player.latest_stats.ACWR)"
      >
        {{ player.latest_stats.ACWR }}
      </table-bar-value>
    </td>
    <td v-if="hasItlModule">
      <table-bar-value
        :value="player.latest_stats.DL || player.latest_stats.DL_1"
        :max="maxValues.DL || maxValues.DL_1"
        color="grey lighten-2"
      >
        {{ player.latest_stats.DL || player.latest_stats.DL_1 }}
      </table-bar-value>
    </td>
    <td v-if="hasItlModule">
      <sparkline-chart
        v-if="player.latest_stats.DL_trend_3days && player.latest_stats.DL_trend_3days.length > 0"
        :series="[ { name: $t('chart_legends.daily_load'), data: player.latest_stats.DL_trend_3days }]"
        :color="dlTrendColor"
        :height="40"
        :width="80"
        style="padding: 5px"
      />
      <div
        v-else
        class="grey--text caption"
      >
        N/A
      </div>
    </td>
    <td v-if="hasItlModule && showWellnessData">
      <table-selection-chip-value
        :value="player.latest_stats.MOO"
        :max="5"
      />
    </td>
    <td v-if="hasItlModule && showWellnessData">
      <table-selection-chip-value
        :value="player.latest_stats.FAT"
        :max="5"
        reverse-colors
      />
    </td>
    <td v-if="hasItlModule && showWellnessData">
      <table-bar-value
        :value="player.latest_stats.player_wellness"
        :max="100"
        :color="player.latest_stats.player_wellness >= 90 ? 'green' : (player.latest_stats.player_wellness > 70 ? 'orange' : 'red')"
      >
        {{ player.latest_stats.player_wellness }}%
      </table-bar-value>
    </td>
    <td v-if="hasItlModule">
      <pain-label
        :label="player.latest_stats.highest_pain ? player.latest_stats.highest_pain.label : undefined"
        :value="player.latest_stats.highest_pain ? player.latest_stats.highest_pain.value : undefined"
        :player-stats="player.latest_stats"
        :gender="player.gender"
      />
    </td>
    <td v-if="hasItlModule">
      <sickness-label :player-stats="player.latest_stats" />
    </td>
  </tr>
</template>

<script>

import PainLabel from '@/components/helpers/PainLabel'
import TableBarValue from '@/components/helpers/TableBarValue'
import TableSelectionChipValue from '@/components/helpers/TableSelectionChipValue'
import SparklineChart from '@/components/charts/SparklineChart'
import { getColorForValueWithDefinition } from '@/utils/helpers'
import { acwrColorsDefinition } from '@/data/models/Stats'
import SicknessLabel from '@/components/helpers/SicknessLabel.vue'

export default {
  name: 'PlayersListTableRow',
  components: { SicknessLabel, SparklineChart, TableSelectionChipValue, TableBarValue, PainLabel },
  props: {
    player: {
      type: Object,
      required: true
    },
    maxValues: {
      type: Object,
      required: true
    }
  },
  computed: {
    dlTrendColor() {
      if (!this.player.latest_stats.DL_trend_3days ||
        !Array.isArray(this.player.latest_stats.DL_trend_3days) ||
        this.player.latest_stats.DL_trend_3days.length < 2) return undefined

      if (this.player.latest_stats.DL_trend_3days[2] > this.player.latest_stats.DL_trend_3days[0]) {
        return '#ff5252'
      }

      return '#4caf50'
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    isIndividualSport() {
      return this.user.sport.is_individual
    },
    showWellnessData() {
      return !this.user.organisation_settings.has_short_wellness_feedback
    },
    hasItlModule() {
      return this.user.organisation_settings.has_itl_module || false
    }
  },
  methods: {
    onClick(e) {
      this.$emit('click', this.player.id, e)
    },
    getAcwrColorFromValue(value) {
      return getColorForValueWithDefinition(value, acwrColorsDefinition)
    }
  }
}
</script>
